import {
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import ProfileScreen from '@/karehero-app/care/ProfileScreen';
import Card from '@/components/wrapped/Card';
import ErrorState from '@/components/mui/ErrorState';
import ShortlistHome from '@/karehero-app/care/shortlists/ShortlistHome';
import ShortlistView from '@/karehero-app/care/shortlists/ShortlistView';
import ProviderDetail from '@/karehero-app/care/shortlists/ProviderDetail';
import ProviderCard from '@/karehero-app/care/shortlists/ProviderCard';
import {
  SHORTLISTS,
  providerById,
  shortlistById,
} from '@/karehero-app/care/shortlists/fixtures';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

const RESIDENTIAL = shortlistById('residential-nursing');
const DEMENTIA = shortlistById('dementia-specialists');
const OAKWOOD = providerById('residential-nursing', 'oakwood'); // Secured
const BRAMBLE = providerById('residential-nursing', 'bramble'); // Unavailable

const home = (over?: Partial<Parameters<typeof ShortlistHome>[0]>) => (
  <ShortlistHome
    shortlists={SHORTLISTS}
    onBack={noop}
    onOpen={noop}
    {...over}
  />
);

// The mobile-shortlist-viewing handover (mini-PRD V1), rendered live from the
// prototype components. Writing the next feature's handover = a file like this.
export const careShortlistsSpec: HandoverSpec = {
  id: 'care-shortlists',
  title: 'Care search v2',
  description:
    'Mobile shortlist viewing for care circles: family members browse the providers their care expert has published, read the notes, open full provider detail, and get in touch to book a visit. The CAM sets every status; the family can mark a home Secured (and clear it) from its card — securing more than one is fine. Dismissed providers never reach this view.',
  links: [
    {
      label: 'Mini-PRD (Linear)',
      url: 'https://linear.app/karehero/document/mini-prd-mobile-shortlist-viewing-for-care-circles-35cbcd6463c0',
    },
    {
      label: 'CAM-side prototype (care search)',
      url: '/care-search',
    },
    {
      label: 'Live flow (Profile › Tools › Care shortlist)',
      url: '/karehero-app/profile',
    },
  ],
  flows: [
    {
      title: 'Browse a published shortlist',
      steps: [
        {
          id: 'tools',
          title: 'Tools tab',
          tap: 'Care › Tools',
          render: () => <ProfileScreen initialTab='tools' />,
        },
        {
          id: 'home',
          title: 'Shortlist home',
          tap: 'Tap ‘Care shortlist’',
          render: () => home(),
        },
        {
          id: 'view',
          title: 'Shortlist view',
          tap: 'Tap ‘Residential with nursing’',
          render: () => (
            <ShortlistView
              shortlist={RESIDENTIAL}
              onBack={noop}
              onOpenProvider={noop}
              onToggleSecured={noop}
            />
          ),
        },
        {
          id: 'detail',
          title: 'Provider detail',
          tap: 'Tap Oakwood care home',
          render: () => <ProviderDetail provider={OAKWOOD} onBack={noop} />,
        },
        {
          id: 'book',
          title: 'Book a visit',
          tap: 'Tap ‘Book a visit’',
          render: () => (
            <ProviderDetail provider={OAKWOOD} onBack={noop} initialBooking />
          ),
        },
      ],
    },
  ],
  states: [
    {
      id: 'home-populated',
      title: 'Home · published shortlists',
      kind: 'happy',
      repro: 'Default fixtures (two published lists)',
      render: () => home(),
    },
    {
      id: 'view-statuses',
      title: 'All four provider statuses',
      kind: 'happy',
      repro:
        'Residential with nursing: Secured / Contacted / Not contacted / Unavailable',
      render: () => (
        <ShortlistView
          shortlist={RESIDENTIAL}
          onBack={noop}
          onOpenProvider={noop}
          onToggleSecured={noop}
        />
      ),
    },
    {
      id: 'home-empty',
      title: 'No shortlists yet',
      kind: 'empty',
      repro: 'shortlists={[]}, the first visit before anything is published',
      render: () => home({ shortlists: [] }),
    },
    {
      id: 'detail-unavailable',
      title: 'Unavailable provider',
      kind: 'edge',
      repro: 'Bramble court: marked, muted on cards, no Book a visit CTA',
      render: () => <ProviderDetail provider={BRAMBLE} onBack={noop} />,
    },
    {
      id: 'view-single',
      title: 'Small shortlist',
      kind: 'edge',
      repro: 'Dementia specialists: two providers, same layout',
      render: () => (
        <ShortlistView
          shortlist={DEMENTIA}
          onBack={noop}
          onOpenProvider={noop}
          onToggleSecured={noop}
        />
      ),
    },
    {
      id: 'home-error',
      title: 'Failed to load',
      kind: 'error',
      repro:
        'error prop on ShortlistHome, using the generic ErrorState pattern',
      render: () => home({ error: true }),
    },
  ],
  requirements: [
    {
      text: 'Shortlist home lists every published shortlist with name, provider count and last updated; drafts never appear',
      status: 'built',
      where: 'Shortlist home',
    },
    {
      text: 'Provider cards: cover photo, name, address, care type chips, regulator rating, distance, price, CAM note, in the CAM’s published order',
      status: 'built',
      where: 'Shortlist view',
    },
    {
      text: 'Provider status on every card: Not contacted / Contacted / Secured / Unavailable; Secured visually prominent; Unavailable shown but clearly marked. The CAM sets every status; the family can only toggle Secured',
      status: 'built',
      where: 'Shortlist view + provider detail',
    },
    {
      text: 'Family marks a home Secured (or clears it) from its card, more than one allowed; clearing reverts to the previous status, or Contacted if none was recorded. The status is shared across every list the home appears in, and the home screen shows a secured count per list',
      status: 'built',
      where: 'Provider card + shortlist home',
    },
    {
      text: 'Dismissed providers are never shown to care circles',
      status: 'built',
      note: 'Fixtures model published output only. Dismissed items have no representation user-side',
    },
    {
      text: 'Provider detail: full parity with admin view minus controls (photos, stats, contact, description, care types, services, features, owner quote, pricing/funding, inspection breakdown)',
      status: 'built',
      where: 'Provider detail',
    },
    {
      text: 'Phone number is tappable (click-to-call)',
      status: 'built',
      where: 'Provider detail contact + Book a visit sheet',
    },
    {
      text: 'Multiple shortlists, independently viewable, clear navigation back to the shortlist home',
      status: 'built',
      where: 'Home ↔ view ↔ detail push stack',
    },
    {
      text: 'Empty state sets expectations before anything is published',
      status: 'built',
      where: 'No shortlists yet state',
    },
    {
      text: 'Contact events (number viewed / called / emailed) tracked for the admin side',
      status: 'partial',
      note: 'tel:/mailto: affordances exist; no event capture in the prototype',
    },
    {
      text: 'Notification when a shortlist is published, deep-linking into it',
      status: 'not-built',
      note: 'Channels are an open PRD question (push/email/in-app)',
    },
    {
      text: 'Publishing auto-creates a “Review your shortlist” action for the care circle',
      status: 'not-built',
      note: 'Would land in the Actions tab; needs the linked-navigation pattern',
    },
    {
      text: 'Care circle comments / reactions on providers',
      status: 'out-of-scope',
      note: 'Stretch. Moves together with the admin threaded-notes spec',
    },
    {
      text: 'Compare view, save/favourite, share via link, offline, PDF export',
      status: 'out-of-scope',
    },
  ],
  components: {
    mui: ['Chip', 'ToggleButtonGroup', 'Typography'],
    existing: ['Card', 'BottomSheet', 'PushScreen', 'ErrorState'],
    new: ['ProviderCard'],
  },
  componentPreviews: {
    Chip: () => <Chip label='Secured' size='small' />,
    Typography: () => <Typography variant='h4'>Care shortlists</Typography>,
    Card: () => (
      <Card title='Residential with nursing' subtitle='Updated 28th May' />
    ),
    ToggleButtonGroup: () => (
      <ToggleButtonGroup
        className='segmented'
        exclusive
        value='calls'
        onChange={noop}
      >
        <ToggleButton value='calls'>Calls</ToggleButton>
        <ToggleButton value='docs'>Docs</ToggleButton>
      </ToggleButtonGroup>
    ),
    ErrorState: () => (
      <ErrorState
        title='Couldn’t load shortlists'
        body='Check your connection and try again.'
        onRetry={noop}
      />
    ),
    ProviderCard: () => (
      <ProviderCard provider={OAKWOOD} onOpen={noop} onToggleSecured={noop} />
    ),
  },
};
